import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { useParams } from 'react-router'
import {
  MiniMap,
  addEdge,
  type Edge,
  type Node as ReactFlowNode,
  Connection,
  NodeChange,
  useReactFlow,
  applyNodeChanges,
  ReactFlowInstance,
  ReactFlow,
} from '@xyflow/react'
import Loader from '../../../shared/Loader'
import NodeDrawer from '../NodeDrawer/index'
import useAutoLayout from '../../../../hooks/useAutoLayout'
import { buildNode } from '../../../../utils/nodeBuilder'
import { PlaybookContext } from '../../../../contexts/playbooks.context'
import {
  DirectionTypes,
  IPlaybook,
  SpexNode,
} from '../../../../types/playbook.type'
import { createNode } from '../../../../services/node.service'
import { ControlsStyled, nodeTypeComponents } from '../../../../utils/constants'
import { edgeTypeComponents } from '../LabeledEdge'
import ErrorPage from '../../../shared/ErrorPage'
import { hashids } from '../PlaybookTable'
import {
  getNewNodePosition,
  mergeRefs,
  findNodeType,
} from '../../../../utils/functions'
import {
  INodeType,
  INodeResponse,
} from '../../../../../shared/types/api/playbook.type'

import '@xyflow/react/dist/style.css'
import '@xyflow/react/dist/base.css'
import styles from './styles.module.scss'
import { alpha, useTheme } from '@mui/material'

interface IProps {
  isLightMode: boolean
  isLayoutEditing: boolean
  children: JSX.Element
  onAdd: (playbook: IPlaybook) => void
}

const proOptions = {
  account: 'paid-pro',
  hideAttribution: true,
}

const Flow: FC<IProps> = ({
  isLightMode,
  isLayoutEditing,
  children,
  onAdd,
}) => {
  const theme = useTheme()

  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null)
  const [currentNode, setCurrentNode] = useState<SpexNode | null>(null)
  const [currentEdge, setCurrentEdge] = useState<Edge | null>(null)
  const [editingNodeType, setEditingNodeType] = useState<INodeType | null>(null)
  const [nodeData, setNodeData] = useState({
    type: '',
    x: 0,
    y: 0,
    label: '',
  })
  const [edgeData, setEdgeData] = useState({
    label: '',
  })

  const ref = useRef<any>(null)
  const { fitView } = useReactFlow()
  const { playbookId = '0' } = useParams()

  const {
    playbook,
    isLoading,
    edges,
    error,
    nodeTypes,
    reactFlowNodes,
    setReactFlowNodes,
    spexNodes,
    setSpexNodes,
    setEdges,
    handleUpdate,
    setPlaybook,
    getPlaybook,
    savePlaybook,
  } = useContext(PlaybookContext)

  useAutoLayout(isLayoutEditing)

  const [, drop] = useDrop({
    accept: 'draggableItem',
    drop: async (item: { type: string; label: string }, monitor: any) => {
      if (!reactFlowInstance) return
      if (!playbook) return

      const position = getNewNodePosition(
        reactFlowNodes,
        playbook?.direction ?? DirectionTypes.VERTICAL,
      )

      const resp: INodeResponse = await createNode(playbook.id, {
        id: 'ignored',
        type: item.type,
        view: 'plain',
        position: position,
        data: {
          label: item.label,
        },
      })
      if (!resp.success) throw new Error(resp.message)
      const newNode = resp.data

      const reactNode = buildNode(
        newNode.id.toString(),
        newNode.x,
        newNode.y,
        newNode.data,
        newNode.type,
      )
      setCurrentEdge(null)
      setCurrentNode(reactNode)
      setEditingNodeType(findNodeType(item.type, nodeTypes)!)

      // TODO: What is this for anyway?
      setNodeData({
        type: item.type,
        x: position.x,
        y: position.y,
        label: item.label,
      })

      const oldSpexNodes = reactFlowNodesAsSpex()
      playbook.nodes = [...oldSpexNodes, reactNode]

      setPlaybook(() => playbook)

      handleUpdate(playbook)
    },
  })

  const nodeChangeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const reactFlowNodesAsSpex = useCallback(
    () =>
      reactFlowNodes.map((n) => {
        const n2: SpexNode = spexNodes[parseInt(n.id)]
        return n2
      }),
    [reactFlowNodes, spexNodes],
  )

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setReactFlowNodes((nds) => {
        return applyNodeChanges(changes, nds)
      })
      setSpexNodes((nds) => {
        const ret = { ...nds }
        changes.forEach((ch: NodeChange) => {
          if (ch.type === 'add') return

          const id = parseInt(ch.id)
          const n: SpexNode = nds[id]
          switch (ch.type) {
            case 'position':
              if (ch.position) n.position = ch.position
              break
          }
          ret[id] = n
        })
        return ret
      })

      if (changes.find((ch: NodeChange) => ch.type === 'position')) {
        if (nodeChangeDebounceRef.current) {
          clearTimeout(nodeChangeDebounceRef.current)
        }

        nodeChangeDebounceRef.current = setTimeout(async () => {
          await savePlaybook({
            ...playbook,
            nodes: reactFlowNodesAsSpex(),
          } as IPlaybook)
        }, 500)
      }
    },
    [
      playbook,
      savePlaybook,
      setReactFlowNodes,
      setSpexNodes,
      reactFlowNodesAsSpex,
    ],
  )

  const onConnect = useCallback(
    async (connection: Connection) => {
      let newEdges

      setEdges((eds) => {
        newEdges = addEdge(
          {
            ...connection,
            id: `${connection.source}-${connection.target}`,
            animated: playbook?.status === 'running',
            style: {
              fontSize: 20,
              stroke:
                playbook?.status === 'running'
                  ? 'green'
                  : isLightMode
                    ? 'black'
                    : 'white',
              strokeWidth: 3,
            },
          },
          eds,
        )

        return newEdges
      })

      const newPlaybook = {
        ...playbook,
        nodes: reactFlowNodes,
        edges: newEdges as unknown as Array<
          Edge & { label: string; data: any }
        >,
      } as IPlaybook

      await savePlaybook(newPlaybook)
    },
    [setEdges, playbook, reactFlowNodes, savePlaybook, isLightMode],
  )

  const handleClose = () => {
    setCurrentNode(null)
    setCurrentEdge(null)
  }

  const onNodeClick = (_: any, node: ReactFlowNode) => {
    setNodeData({
      type: node.type || '',
      x: node.position.x,
      y: node.position.y,
      label: '',
    })

    setCurrentEdge(null)
    setCurrentNode(node as unknown as SpexNode)
    if (node.type) setEditingNodeType(findNodeType(node.type, nodeTypes)!)
  }

  const onEdgeClick = (_: any, edge: Edge) => {
    setEdgeData({
      label: (edge.data?.label as string) || '',
    })

    setCurrentNode(null)
    setCurrentEdge(edge)
  }

  useEffect(() => {
    if (playbookId) {
      const decoded = hashids.decode(playbookId)
      const id =
        Array.isArray(decoded) && decoded.length > 0 ? decoded[0] : null

      if (id) {
        getPlaybook(`${id}`)
      }
    }

    return () => {
      setReactFlowNodes([])
      setSpexNodes({})
      setEdges([])
      setPlaybook(undefined)
    }
  }, [playbookId])

  useEffect(() => {
    if (isLayoutEditing) fitView()
  }, [
    reactFlowNodes,
    edges,
    fitView,
    isLayoutEditing,
    playbook?.auto_layout_options,
  ])

  if (isLoading) {
    return <Loader />
  }

  if (error) {
    return <ErrorPage code={400} message={error} />
  }

  return (
    <div className={styles.chart}>
      <ReactFlow
        fitView
        maxZoom={10}
        minZoom={0.1}
        nodes={reactFlowNodes}
        edges={edges}
        snapToGrid={true}
        snapGrid={[20, 20]}
        nodeTypes={nodeTypeComponents}
        edgeTypes={edgeTypeComponents}
        proOptions={proOptions}
        ref={mergeRefs(ref, drop)}
        nodesDraggable
        onInit={setReactFlowInstance}
        nodesConnectable
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        deleteKeyCode={null}
        onNodesChange={onNodesChange}
        defaultEdgeOptions={{
          style: { strokeWidth: 3 },
          labelStyle: {
            fontSize: 20,
            stroke: '#49bbb3',
            strokeWidth: 3,
          },
          labelShowBg: false,
        }}
      >
        <MiniMap
          nodeColor={alpha(theme.palette.primary.main, 0.8)}
          style={
            {
              background: theme.palette.background.default,
              '--xy-minimap-mask-background-color-default': alpha(
                theme.palette.text.primary,
                theme.palette.mode === 'dark' ? 0.35 : 0.08,
              ),
            } as React.CSSProperties
          }
          draggable
          pannable
        />
        <ControlsStyled />
        {children}
      </ReactFlow>
      {(currentNode || currentEdge) && (
        <NodeDrawer
          type={nodeData.type}
          label={currentNode ? nodeData.label : edgeData.label}
          nodeType={editingNodeType}
          nodeId={currentNode?.id}
          edgeId={currentEdge?.id}
          defaultData={currentNode?.data || currentEdge?.data || {}}
          handleClose={handleClose}
        />
      )}
    </div>
  )
}

export default Flow
